/* eslint-disable better-mutation/no-mutation */

import { isServer } from 'tradera/utils/nextjs';
import { parseJwt } from 'tradera/apps/registration/helpers/parse-jwt';
import { loadScript } from 'tradera/utils/script';
import * as ga from '../../services/login-signup-analytics';

if (isServer) {
    // Do not remove this without also removing the disable of eslint better-mutation/no-mutation at top of file.
    throw new Error(
        'This implementation is not compatible with SSR as global properties are shared between server instances'
    );
}

// these functions are available in the android/ios app code base
(function (w) {
    if (!w) {
        return;
    }
    let saved = null;
    let savedListener = null;
    w.onKlarnaNativeLogin = (err, data) => {
        if (savedListener) {
            savedListener(err, data);
        } else {
            saved = { err: err, data: data };
        }
    };
    w.onKlarnaNativeLoginSetListener = (listener) => {
        savedListener = listener;
        if (saved) {
            savedListener(saved.err, saved.data);
            saved = null;
        }
    };
})(window);

export default class KlarnaLogin {
    constructor(klarnaAppId, klarnaEnvironment, locale, authenticateCallback) {
        !klarnaAppId &&
            console.error('KlarnaLogin.klarnaAppId is required but was empty');
        !klarnaEnvironment &&
            console.error(
                'KlarnaLogin.klarnaEnvironment is required but was empty'
            );
        this.locale = locale;
        this.appId = klarnaAppId;
        this.environment = klarnaEnvironment;
        this.email = null;
        this.firstName = null;
        this.lastName = null;
        this.personalNumber = null;
        this.streetName = null;
        this.zipCode = null;
        this.town = null;
        this.phoneNumber = null;
        this.idToken = null;
        this.accessToken = null;
        this.refreshToken = null;

        const redirectUri = new URL(
            '/register',
            window.location.origin
        ).toString();

        loadScript(`https://x.klarnacdn.net/sign-in-with-klarna/v1/lib.js`, {
            dataAttributes: {
                'client-id': this.appId,
                market: 'SE',
                scope: 'offline_access profile email billing_address phone national_identification_number create_payment_session',
                environment: this.environment,
                'on-sign-in': 'onKlarnaSignIn',
                'on-error': 'onKlarnaSignInError',
                locale: this.locale || 'sv-SE',
                'redirect-uri': redirectUri
            }
        });

        window.onKlarnaSignIn = async (tokens) => {
            const { id_token, refresh_token } = tokens;
            this.idToken = id_token;
            this.refreshToken = refresh_token;

            const result = await this.login({ native: false });
            authenticateCallback(result);
        };

        window.onKlarnaSignInError = () => {
            ga.trackEvent('user-signup-klarna-error');
            return;
        };
    }

    onNativeInitiatedLogin(callback) {
        if (!window) {
            return;
        }
        window.onKlarnaNativeLoginSetListener((err, data) => {
            if (err) {
                console.error(
                    'Klarna Native Initiated login failed',
                    err,
                    data
                );
                this._save(null);
            } else {
                this._save(data);
                callback(data);
            }
        });
    }

    async login(options = { native: false }) {
        if (window && options.native) {
            return new Promise((resolve, reject) => {
                window.onKlarnaNativeLoginSetListener((err, data) => {
                    if (err || !data) {
                        console.error('Klarna Native login failed', err, data);
                        this._save(null);
                        reject(err ? err : 'Aborted');
                    } else {
                        const idToken = parseJwt(data.idToken);
                        if (!data.email) {
                            data.email = idToken?.email;
                        }
                        if (!data.userId) {
                            data.userId = idToken?.sub;
                        }
                        if (!data.lastName) {
                            data.lastName = idToken?.family_name;
                        }
                        if (!data.firstName) {
                            data.firstName = idToken?.given_name;
                        }
                        if (!data.nationalIdentificationNumber) {
                            data.personalNumber =
                                idToken?.national_identification_number?.substring(
                                    2
                                );
                        }
                        if (!data.phone) {
                            data.phoneNumber = idToken?.phone;
                        }
                        if (!data.town) {
                            data.town = idToken?.billing_address?.city;
                        }
                        if (!data.streetName) {
                            data.streetName =
                                idToken?.billing_address?.street_address;
                        }
                        if (!data.zipCode) {
                            data.zipCode =
                                idToken?.billing_address?.postal_code?.replace(
                                    /\s+/g,
                                    ''
                                );
                        }

                        //Explicitly overwrites the accessToken with the idToken
                        //TODO rename accessToken to data.accessToken to data.idToken
                        if (data.idToken) {
                            data.accessToken = data.idToken;
                        }

                        this._save(data);
                        resolve(data);
                    }
                });
                window.document.location.href =
                    'tradera://register/klarna?callbackFunc=onKlarnaNativeLogin';
            });
        } else {
            const idToken = parseJwt(this.idToken);
            return {
                accessToken: this.idToken, //TODO rename accessToken to idToken
                refreshToken: this.refreshToken,
                email: idToken.email,
                firstName: idToken.given_name,
                lastName: idToken.family_name,
                personalNumber:
                    idToken.national_identification_number?.substring(2),
                phoneNumber: idToken.phone,
                town: idToken.billing_address.city,
                streetName: idToken.billing_address.street_address,
                zipCode: idToken.billing_address.postal_code?.replace(
                    /\s+/g,
                    ''
                ),
                userId: idToken.sub
            };
        }
    }

    _save(data) {
        if (!data) {
            this.email = null;
            this.firstName = null;
            this.lastName = null;
            this.personalNumber = null;
            this.streetName = null;
            this.zipCode = null;
            this.town = null;
            this.phoneNumber = null;
            this.userId = null;
            this.accessToken = null;
        } else {
            this.accessToken = data.accessToken;
            this.email = data.email;
            this.firstName = data.firstName;
            this.lastName = data.lastName;
            this.personalNumber = data.personalNumber;
            this.streetName = data.streetName;
            this.zipCode = data.zipCode;
            this.town = data.town;
            this.phoneNumber = data.phoneNumber;
            this.userId = data.userId;
        }
    }
}
